import classNames from 'classnames';
import { Fragment, memo } from 'react';
import styled from 'styled-components';

import { FilterAccordion } from '@sorare/core/src/atoms/layout/accordion/FilterAccordion';
import { Vertical } from '@sorare/core/src/atoms/layout/flex';
import type {
  FilterSeparator,
  FilterWidget,
  FiltersGroup as FiltersGroupType,
} from '@sorare/core/src/lib/filters';

import { ClearAllFilters } from '#src/search/ClearAllFilters/index.tsx';
import { FiltersGroup } from '#src/search/FiltersGroup/index.tsx';

type Props = {
  filters: FiltersGroupType[];
  skipClearAllButton?: boolean;
};

const FilterRoot = styled.div`
  &:not(:has(.FilterWidget.visible)) {
    display: none;
  }
`;

const Separator = styled.div`
  border-top: 1px solid var(--c-nd-50);
`;
const Footer = styled.div`
  &.withBorder {
    border-top: 1px solid var(--c-nd-50);
  }
  padding: var(--double-unit) 0;
`;

const Filter = ({ filter }: { filter: FilterWidget | FilterSeparator }) => {
  if (filter.type === 'separator') {
    return <Separator />;
  }
  if (filter.type === 'raw') {
    return (
      <Fragment key={filter.key}>
        <filter.Component visible {...filter.props} />
      </Fragment>
    );
  }
  return (
    <FilterRoot key={filter.key}>
      <FilterAccordion
        title={filter.title}
        startsOpen={filter.accordionOptions?.startsOpen}
      >
        {({ expanded }) => (
          <filter.Component visible={expanded} {...filter.props} />
        )}
      </FilterAccordion>
    </FilterRoot>
  );
};

export const Filters = ({ filters }: { filters: Props['filters'] }) => {
  return (
    <Vertical gap={2}>
      {filters.map(filtersGroup => {
        if (filtersGroup.filter) {
          return (
            <Fragment key={filtersGroup.slug}>
              <filtersGroup.filter.Component
                visible
                {...filtersGroup.filter.props}
              />
            </Fragment>
          );
        }

        return (
          <FiltersGroup key={filtersGroup.slug} title={filtersGroup.title}>
            {filtersGroup.filters?.map((filter, ii) => {
              const key =
                filter.type === 'separator' ? `separator-${ii}` : filter.key;
              return <Filter key={key} filter={filter} />;
            })}
          </FiltersGroup>
        );
      })}
    </Vertical>
  );
};

export const FiltersManager = memo(function FiltersManager({
  filters,
  skipClearAllButton,
}: Props) {
  return (
    <Vertical gap={0}>
      <Filters filters={filters} />
      {!skipClearAllButton && (
        <Footer className={classNames({ withBorder: true })}>
          <ClearAllFilters />
        </Footer>
      )}
    </Vertical>
  );
});
